import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Leaf, ShieldCheck, Sparkles } from "lucide-react";

const MARQUEE_TAGS = ["Paper Cups", "Tissue Napkins", "Food Containers", "Custom Printing", "Bulk Supply", "Pan India Delivery", "FSSAI Compliant", "Eco-Friendly", "ISO Quality"];
const MARQUEE_TRACKS = ["track-a", "track-b"];

export const Hero = ({ onQuote, onExplore }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 vc-grid-bg pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full vc-gradient opacity-20 blur-3xl pointer-events-none" />
      <div className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-400 opacity-10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white/70 backdrop-blur px-3 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold tracking-wide text-neutral-700">
              Manufacturing • Dharwad, Karnataka • Since 2023
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] font-extrabold text-neutral-900">
            Sustainable Disposable <br />
            Solutions for a{" "}
            <span className="vc-text-gradient">Better Tomorrow.</span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-neutral-600 max-w-2xl leading-relaxed">
            Manufacturing premium Paper Cups, Tissue Napkins, and Eco-Friendly Packaging
            Products for businesses across India — engineered for hygiene, branded for impact.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onExplore}
              data-testid="hero-explore-btn"
              className="vc-gradient text-white rounded-full h-12 px-7 text-base font-semibold border-0 group"
            >
              Explore Products
              <ArrowUpRight className="ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" size={18} />
            </Button>
            <Button
              onClick={onQuote}
              data-testid="hero-quote-btn"
              variant="outline"
              className="rounded-full h-12 px-7 text-base font-semibold border-neutral-300 hover:bg-neutral-50"
            >
              Request a Quote
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Badge icon={<ShieldCheck size={16} />} label="100% Food Safe" />
            <Badge icon={<Leaf size={16} />} label="Eco-Friendly" />
            <Badge icon={<Sparkles size={16} />} label="Custom Printing" />
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-neutral-200 shadow-2xl shadow-orange-500/10">
            <img
              src="https://images.unsplash.com/photo-1615485736894-a2d2e6d4cd9a?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Paper cups production"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-neutral-900/30 via-transparent to-transparent" />

            <div className="absolute left-5 bottom-5 right-5 bg-white/85 backdrop-blur-xl rounded-2xl p-4 border border-white/60 shadow-lg">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-mono-vc uppercase tracking-[0.2em] text-neutral-500">Live Production</div>
                  <div className="text-lg font-display font-bold text-neutral-900 mt-0.5">1.2M+ cups / month</div>
                </div>
                <div className="w-14 h-14 rounded-full vc-gradient flex items-center justify-center">
                  <Sparkles className="text-white" size={22} />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -top-6 -left-6 vc-float">
            <div className="bg-white border border-neutral-200 rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Leaf className="text-emerald-600" size={18} />
              </div>
              <div>
                <div className="text-xs text-neutral-500 font-medium">Tree-friendly</div>
                <div className="text-sm font-bold text-neutral-900">FSC Certified</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-20 border-y border-neutral-200 py-5 overflow-hidden">
        <div className="flex gap-16 vc-marquee whitespace-nowrap">
          {MARQUEE_TRACKS.map((trackId) => (
            <div key={trackId} className="flex gap-16 items-center" aria-hidden={trackId === "track-b"}>
              {MARQUEE_TAGS.map((t) => (
                <span key={`${trackId}-${t}`} className="font-display text-2xl md:text-3xl font-bold text-neutral-300">
                  {t} <span className="vc-text-gradient">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Badge = ({ icon, label }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
    <span className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-700">
      {icon}
    </span>
    {label}
  </div>
);
